import {
  Checkbox,
  CheckboxState,
} from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { displayedConnectorType } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/displayed-connector-type';
import { useCreatePhysicalSocketFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.form-validation';
import { CreatePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.model';
import {
  ConnectorFormat,
  ConnectorType,
  PowerType,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DC_DEFAULT_VOLTAGE,
  isDcConnector,
} from '@app/feature/charge-point/helper/connector-dc.helper';
import { PhysicalSocketDcPowerType } from '@app/feature/charge-point/physical-socket-dc-field/physical-socket-dc-power-type.component';
import { PhysicalSocketDcVoltage } from '@app/feature/charge-point/physical-socket-dc-field/physical-socket-dc-voltage.component';

import * as css from './charge-point-channel-physical-socket-create-form-step-1.module.scss';

const ChargePointChannelPhysicalSocketFragment = (): React.JSX.Element => {
  const { t } = useTranslation();

  const { control, setValue, watch } =
    useFormContext<
      Pick<
        CreatePhysicalSocketModel,
        'chargePointId' | 'channelId' | 'physicalSocket'
      >
    >();

  const {
    physicalSocketStandard,
    physicalSocketPowerType,
    physicalSocketVoltage,
    physicalSocketAmperage,
    physicalSocketFormat,
  } = useCreatePhysicalSocketFormValidation();

  // WATCHERS //////////////////////////////////////////////////////////////////

  const physicalSocketPowerTypeValue = watch(`physicalSocket.powerType`);
  const physicalSocketAmperageValue = watch(`physicalSocket.amperage`);
  const physicalSocketVoltageValue = watch(`physicalSocket.voltage`);

  const standardValue = watch('physicalSocket.standard');
  const isDc = isDcConnector(standardValue);

  const handlePhysicalSocketStandardChange = (
    physicalSocketStandardNew: ConnectorType,
  ) => {
    if (physicalSocketStandardNew === ConnectorType.CHADEMO) {
      setValue('physicalSocket.powerType', PowerType.AC_3_PHASE, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue('physicalSocket.format', ConnectorFormat.SOCKET, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(`physicalSocket.voltage`, 400, {
        shouldDirty: true,
        shouldValidate: true,
      });

      return;
    }

    if (isDcConnector(physicalSocketStandardNew)) {
      setValue(`physicalSocket.powerType`, PowerType.DC, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(`physicalSocket.format`, ConnectorFormat.CABLE, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(`physicalSocket.voltage`, DC_DEFAULT_VOLTAGE, {
        shouldDirty: true,
        shouldValidate: true,
      });

      return;
    }

    if (
      physicalSocketStandardNew &&
      [
        ConnectorType.DOMESTIC_E,
        ConnectorType.DOMESTIC_F,
        ConnectorType.IEC_62196_T2,
      ].includes(physicalSocketStandardNew)
    ) {
      setValue(`physicalSocket.powerType`, PowerType.AC_1_PHASE, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(`physicalSocket.format`, ConnectorFormat.SOCKET, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(`physicalSocket.voltage`, 230, {
        shouldDirty: true,
        shouldValidate: true,
      });

      return;
    }

    setValue(`physicalSocket.format`, ConnectorFormat.SOCKET, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const physicalSocketRealVoltageValue =
    (physicalSocketPowerTypeValue === PowerType.AC_3_PHASE
      ? computeTreePhaseVoltage(parseInt(`${physicalSocketVoltageValue}`, 10))
      : physicalSocketVoltageValue) ?? 0;

  return (
    <Stack direction="column" gap={GapEnum.M}>
      <Controller
        name="physicalSocket.standard"
        rules={physicalSocketStandard}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Dropdown
            label={t('delmonicos-shared-sdk.outputs-physical-socket.standard')}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-physical-socket.standard',
            )}
            required
            removable
            errorMessage={error?.message}
            value={field.value ?? ''}
            onChange={(value) => {
              handlePhysicalSocketStandardChange(value as ConnectorType);
              field.onChange(value ?? '');
            }}>
            {displayedConnectorType.map((s) => (
              <Dropdown.Option key={s} value={s}>
                {t(
                  `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type.${s}`,
                )}
              </Dropdown.Option>
            ))}
          </Dropdown>
        )}
      />
      <Stack gap={GapEnum.XS} direction="row">
        {isDc ? (
          <PhysicalSocketDcPowerType value={physicalSocketPowerTypeValue} />
        ) : (
          <Controller
            name="physicalSocket.powerType"
            control={control}
            rules={physicalSocketPowerType}
            render={({ field, fieldState: { error } }) => (
              <Dropdown
                label={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.power-type',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.power-type',
                )}
                required
                removable
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(value) => field.onChange(value ?? '')}>
                {Object.values(PowerType).map((s) => (
                  <Dropdown.Option key={s} value={s}>
                    {t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.power-type.${s}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
            )}
          />
        )}
        <Controller
          name="physicalSocket.voltage"
          control={control}
          rules={physicalSocketVoltage}
          render={({ field, fieldState: { error } }) => {
            return isDc ? (
              <PhysicalSocketDcVoltage
                required={!!physicalSocketVoltage.required}
                errorMessage={error?.message}
                value={field.value}
                onChange={field.onChange}
              />
            ) : (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.voltage',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.voltage',
                )}
                type="number"
                required={!!physicalSocketVoltage.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Controller
          name="physicalSocket.amperage"
          control={control}
          rules={physicalSocketAmperage}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.amperage',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-physical-socket.amperage',
                )}
                type="number"
                required={!!physicalSocketAmperage.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <TextInput
          className={css.readOnly}
          readOnly
          label={t('$.charge-point-create.step-2.physical-sockets.power-label')}
          value={t('$.charge-point-create.step-2.physical-sockets.power', {
            power:
              physicalSocketPowerType &&
              physicalSocketAmperageValue &&
              physicalSocketVoltageValue
                ? (
                    (physicalSocketAmperageValue *
                      physicalSocketRealVoltageValue) /
                    1000
                  ).toFixed(0)
                : '--',
          })}
        />
      </Stack>
      <Divider />
      <Controller
        name="physicalSocket.format"
        control={control}
        rules={physicalSocketFormat}
        render={({ field }) => {
          return (
            <Checkbox
              state={isDc ? CheckboxState.Disabled : CheckboxState.Default}
              id="physicalSocket.format"
              label={t(
                `delmonicos-shared-sdk.ocpi-protocol-enums.connector-format-alt.${ConnectorFormat.CABLE}`,
              )}
              checked={field.value === ConnectorFormat.CABLE}
              onChange={(event) => {
                field.onChange(
                  event.currentTarget.checked
                    ? ConnectorFormat.CABLE
                    : ConnectorFormat.SOCKET,
                );
              }}
            />
          );
        }}
      />
    </Stack>
  );
};

export const ChargePointChannelPhysicalSocketCreateFormStep1Controller =
  (): React.JSX.Element => {
    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Stack
        className={css.chargePointChannelPhysicalSocketFormStep1}
        direction="column"
        gap={GapEnum.L}>
        <Stack
          direction="row"
          justifyContent="stretch"
          alignItems="stretch"
          flex={1}
          gap={GapEnum.XL}>
          <Stack
            direction="column"
            flex={1}
            alignItems="stretch"
            gap={GapEnum.XL}>
            <ChargePointChannelPhysicalSocketFragment />
          </Stack>
        </Stack>
      </Stack>
    );
  };
